import moment from "moment";
import { getSalesByLevel3, getSalesCharts, getSalesComparison } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";
import { normalizeSalesCharts, deriveByClientSeries } from "./utils/salesCharts";

// El chip "Clientes" incluye las notas de ingreso y las ventas devengadas
// (plantilla estándar): son ventas cuyas contrapartes son tipo 'Nota de
// Venta' y 'clientes-v-cv', no 'Cliente' — sin esos slugs, el backend las
// filtraba fuera (final_sql_counterpart_type = ANY(...)) y el modo clientes
// mostraba un total menor que el modo cuentas, sin fila que delatara el delta.
// Se aplica a tabla Y charts (mismo eje).
export const resolveSalesGroupBy = (subFilter) => {
    const groupBy = (subFilter || "").toLowerCase();
    return groupBy === "clientes"
        ? "clientes,nota-venta,clientes-v-cv"
        : groupBy;
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = () => ({
    /** Filtro de agrupación (Cuentas, Clientes, Tiendas, etc.) */
    activeSubFilter: "cuentas",
    /**
     * Cabeceras de columna calculadas tras el fetch.
     * Ejemplo: ["Ene 2025", "Feb 2025", ...]
     * @type {string[]}
     */
    monthHeaders: [],
    /**
     * Dict indexado por la etiqueta de cada fila (cuenta level_3, subcategoría
     * o cliente según el sub-filtro activo — siempre un string simple, lista
     * plana en todos los modos).
     * Cada valor es: { [periodoLabel]: { value: number, prevYear: number|null } }
     * donde `prevYear` es el monto del mismo bucket del año anterior — soporte
     * de YoY a nivel celda en la tabla. `null` cuando no hay peer (etiqueta
     * nueva o sin movimiento hace 12m).
     * @type {Object.<string, Object.<string, { value: number, prevYear: number|null }>>}
     */
    accountsData: {},
    /**
     * Tipo de contraparte por etiqueta de fila (`{ alias: 'Nota de Venta' }`),
     * sólo poblado en modo clientes. Lo usa el drill-down para abrir el drawer
     * con la obligación correcta; `{}` en los demás modos.
     * @type {Object.<string, string>}
     */
    counterpartTypes: {},
    /** Lista ordenada de etiquetas de fila (cuentas / categorías / clientes). */
    accounts: [],
    /**
     * Categorías (tipos de atributo) con clasificación real en el período —
     * las que el backend confirma con datos. El header deshabilita los chips de
     * categoría que no estén acá (sólo tendrían "Sin <categoría>").
     * @type {string[]}
     */
    availableCategories: [],
    /**
     * Items tildados en el filtro multi-select del header (labels del
     * sub-filtro activo). Vacío = sin filtro (se muestran todos). Cuando tiene
     * elementos, la tabla y los gráficos del header se acotan a esos labels.
     * Se resetea al cambiar `activeSubFilter` (los items difieren por
     * dimensión). El filtrado es client-side sobre `accountsData` ya cargado —
     * sin refetch.
     * @type {string[]}
     */
    selectedItems: [],
    /** Rango real devuelto por el backend */
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
    /**
     * Series para los dos gráficos del header (stacked bar + actividad).
     * El backend devuelve cada serie como `[{ period: ISO, total_amount }]`;
     * acá las normalizamos a `[{ label, value }]` donde `label` es el
     * monthHeader formateado en español (coherente con el resto del store)
     * para que el componente de chart no tenga que repetir el parseo de
     * `formatPeriodLabel`. `monthHeaders` se replica acá adentro del shape
     * porque el chart vive en un fetch separado del de la tabla — si los
     * watchers de ambos no se completaron, los headers podrían quedar
     * desalineados.
     * @type {{
     *   monthHeaders: string[],
     *   byClient: { name: string, values: number[] }[],
     *   clientCounts: number[],
     *   avgTickets: number[],
     *   hasOtros: boolean,
     *   topN: number,
     * } | null}
     */
    chartsData: null,
    isChartsLoading: false,
    chartsError: null,
    /**
     * Gráfico de comparación año-contra-año (toggle del botón "Comparación"
     * de la tabla). Vive separado de `chartsData` porque se fetchea lazy
     * (sólo al abrir el panel) y con un contrato distinto: una serie por año
     * calendario alineada a los 12 meses Ene..Dic.
     * @type {{
     *   months: number[],
     *   series: { year: number, values: (number|null)[] }[],
     *   availableYears: number[],
     * } | null}
     */
    comparisonData: null,
    isComparisonLoading: false,
    comparisonError: null,
    /**
     * Años seleccionados para comparar. Vacío hasta la primera apertura;
     * `fetchSalesComparison` lo inicializa a [añoActual, añoActual-1] a partir
     * del `endMonth` del header.
     * @type {number[]}
     */
    comparisonYears: [],
    /**
     * Subcategoría seleccionada en modo categoría (tiendas/marcas). `null` en
     * modo cuentas (no hay subcategoría) o antes de elegir una.
     * @type {string|null}
     */
    comparisonItem: null,
});

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const getters = {
    /**
     * Lista completa de items del sub-filtro activo (cuentas / categorías /
     * clientes), sin acotar por la selección — alimenta el filtro multi-select
     * del header, que siempre debe ofrecer todos los items disponibles.
     * @returns {string[]}
     */
    availableItems: (state) => state.accounts,

    /**
     * Labels visibles según el filtro: los seleccionados (en el orden original
     * de `accounts`) o todos cuando no hay selección. Base compartida por
     * `tableRows`, `totalRow` y `headerChartsData` para que tabla y gráficos
     * se acoten al mismo conjunto.
     * @returns {string[]}
     */
    visibleAccounts: (state) => {
        if (!state.selectedItems.length) return state.accounts;
        const selected = new Set(state.selectedItems);
        return state.accounts.filter((account) => selected.has(account));
    },

    /**
     * Construye las filas planas de la tabla: una fila por etiqueta
     * (cuenta level_3, subcategoría o cliente según el sub-filtro).
     *
     * Cada fila lleva `values` (montos por bucket) y `prevYearValues`
     * (peer del año anterior por bucket; `null` cuando no hay peer). El
     * frontend usa ambos arrays paralelos para computar YoY a nivel celda
     * en el tooltip de cada `<td>`.
     * @returns {Array<{
     *   label: string,
     *   values: number[],
     *   prevYearValues: (number|null)[],
     *   isTotal: boolean,
     * }>}
     */
    tableRows: (state, getters) => {
        const headers = state.monthHeaders;
        // Freeze por fila + el array contenedor: en modo clientes esto puede ser
        // de cientos de miles de filas, y Vue 2 haría observación reactiva
        // profunda de cada objeto al pasarlas como prop (segundos de costo).
        // Congeladas, Vue las saltea — la tabla sólo las lee, nunca las muta, y
        // la virtualización de filas se encarga del render.
        return Object.freeze(getters.visibleAccounts.map((account) => Object.freeze({
            label: account,
            isTotal: false,
            values: Object.freeze(headers.map(
                (h) => state.accountsData[account]?.[h]?.value ?? 0
            )),
            prevYearValues: Object.freeze(headers.map(
                (h) => state.accountsData[account]?.[h]?.prevYear ?? null
            )),
            prevPeriodValues: Object.freeze(headers.map(
                (h) => state.accountsData[account]?.[h]?.prevPeriod ?? null
            )),
        })));
    },

    /**
     * Fila de totales: suma de todas las etiquetas por bucket. El peer del
     * año anterior se deriva igual: suma de prev_year por bucket. Cuando
     * todas las keys tienen `null` en un bucket dado, el peer del total queda
     * `null` (no había nada hace 12m).
     * @returns {{
     *   label: string,
     *   values: number[],
     *   prevYearValues: (number|null)[],
     *   isTotal: boolean,
     * }}
     */
    totalRow: (state, getters) => {
        const summableKeys = getters.visibleAccounts;
        const headers = state.monthHeaders;
        const values = headers.map((h) =>
            summableKeys.reduce(
                (sum, key) => sum + (state.accountsData[key]?.[h]?.value ?? 0),
                0,
            )
        );
        const sumPeer = (h, field) => {
            let sum = 0;
            let anyPeer = false;
            for (const key of summableKeys) {
                const peer = state.accountsData[key]?.[h]?.[field];
                if (peer != null) {
                    sum += peer;
                    anyPeer = true;
                }
            }
            return anyPeer ? sum : null;
        };
        const prevYearValues = headers.map((h) => sumPeer(h, "prevYear"));
        const prevPeriodValues = headers.map((h) => sumPeer(h, "prevPeriod"));
        return {
            label: "Ventas Netas",
            values,
            prevYearValues,
            prevPeriodValues,
            isTotal: true,
        };
    },

    /**
     * Data de los gráficos del header. Sin selección, usa la serie top-N +
     * "Otros" que devuelve el endpoint `charts` (comportamiento histórico).
     * Con selección activa, deriva las series client-side de `accountsData`
     * acotadas a los items elegidos — así el filtro mueve gráficos y tabla en
     * sincronía y sin refetch, incluso para clientes fuera del top-N.
     * @returns {object|null}
     */
    headerChartsData: (state) => {
        if (!state.selectedItems.length) return state.chartsData;
        return deriveByClientSeries(
            state.accountsData,
            state.monthHeaders,
            state.selectedItems,
        );
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_ACTIVE_SUB_FILTER(state, filter) {
        state.activeSubFilter = filter;
        // Los items disponibles cambian de dimensión (cuentas → clientes → …),
        // así que la selección previa deja de aplicar: la limpiamos.
        state.selectedItems = [];
    },
    SET_SELECTED_ITEMS(state, items) {
        state.selectedItems = items;
    },
    SET_SALES_DATA(state, {
        accounts,
        accountsData,
        counterpartTypes,
        monthHeaders,
        periodRange,
        availableCategories,
    }) {
        state.accounts = accounts;
        state.accountsData = accountsData;
        state.counterpartTypes = counterpartTypes || {};
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
        state.availableCategories = availableCategories || [];
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_CHARTS_DATA(state, payload) {
        state.chartsData = payload;
    },
    SET_CHARTS_LOADING(state, isLoading) {
        state.isChartsLoading = isLoading;
    },
    SET_CHARTS_ERROR(state, error) {
        state.chartsError = error;
    },
    SET_COMPARISON_DATA(state, payload) {
        state.comparisonData = payload;
    },
    SET_COMPARISON_LOADING(state, isLoading) {
        state.isComparisonLoading = isLoading;
    },
    SET_COMPARISON_ERROR(state, error) {
        state.comparisonError = error;
    },
    SET_COMPARISON_YEARS(state, years) {
        state.comparisonYears = years;
    },
    SET_COMPARISON_ITEM(state, item) {
        state.comparisonItem = item;
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    // Bootstrap del tab Ventas — el view sólo llama esta action en `mounted`
    // y la action orquesta las dos lecturas (tabla + gráficos) en paralelo.
    // Cada una tiene su propia signature de refetch en watchers, así que se
    // mantienen exportadas individualmente para el watch-driven refresh.
    async initVentas({ dispatch }, payload = {}) {
        await Promise.all([
            dispatch("fetchSalesData"),
            dispatch("fetchSalesCharts", payload),
        ]);
    },

    async fetchSalesData({ state, commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;

            const { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                filterPeriod,
                rootState.reportsFilters.customRange,
                rootState.reportsFilters.customYearRange,
                rootState.reportsFilters.dataFirstMonth,
            );

            // Format groupBy parameter (e.g. 'cuentas', 'clientes,nota-venta',
            // or category name)
            const groupBy = resolveSalesGroupBy(state.activeSubFilter);

            const response = await getSalesByLevel3(initDate, endDate, groupBy, filterPeriod);
            const {
                accounts,
                report_data: reportData,
                period_range: periodRange,
                available_categories: availableCategories,
                // Tipo de contraparte por etiqueta de fila; sólo viene cuando
                // se agrupa por contraparte. El drill-down lo necesita para
                // abrir el drawer con la obligación justa (una nota de ingreso
                // es tipo 'Nota de Venta', no 'Cliente').
                counterpart_types: counterpartTypes,
            } = response.data;

            // Cabeceras según granularidad (mes/trimestre/año).
            const monthHeaders = buildHeadersByPeriod(
                periodRange.init_date,
                periodRange.end_date,
                filterPeriod
            );

            // Lista plana en todos los modos (cuentas / categorías / clientes):
            // una entrada de `report_data` por etiqueta de fila.
            // Por bucket guardamos `{ value, prevYear }`. `prevYear` puede ser
            // `null` (sin peer hace 12m) — distinto de 0 (peer real que neteó
            // a cero). El frontend usa la diferencia: null pinta "—" en el
            // tooltip de YoY, 0 pinta "0,0%".
            const accountsData = {};
            accounts.forEach((key) => {
                const row = {};
                (reportData[key] ?? []).forEach((item) => {
                    const label = formatPeriodLabel(item.period, filterPeriod);
                    row[label] = Object.freeze({
                        value: Number(item.total_amount),
                        prevYear: item.prev_year_amount == null
                            ? null
                            : Number(item.prev_year_amount),
                        // Peer del período anterior (MoM/QoQ) traído de data
                        // real; `undefined` en year/ytd (el backend no lo manda).
                        prevPeriod: item.prev_period_amount == null
                            ? null
                            : Number(item.prev_period_amount),
                    });
                });
                // Freeze por fila: Vue 2 omite el observer recursivo en objetos congelados.
                accountsData[key] = Object.freeze(row);
            });

            commit("SET_SALES_DATA", {
                accounts: Object.freeze(accounts),
                accountsData: Object.freeze(accountsData),
                counterpartTypes: Object.freeze(counterpartTypes || {}),
                monthHeaders: Object.freeze(monthHeaders),
                periodRange: Object.freeze(periodRange),
                availableCategories: Object.freeze(availableCategories || []),
            });
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar ventas."));
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async fetchSalesCharts({ state, commit, rootState }, payload = {}) {
        commit("SET_CHARTS_LOADING", true);
        commit("SET_CHARTS_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;
            const { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange,
                filterPeriod,
                rootState.reportsFilters.customRange,
                rootState.reportsFilters.customYearRange,
                rootState.reportsFilters.dataFirstMonth,
            );

            // Los gráficos del header reflejan el sub-filtro activo: por cuenta,
            // por categoría o por cliente (mismo eje que la tabla y el título).
            const response = await getSalesCharts(
                initDate,
                endDate,
                filterPeriod,
                payload.topN,
                resolveSalesGroupBy(state.activeSubFilter),
            );
            // Normalización compartida (ver utils/salesCharts) → arrays
            // paralelos alineados a monthHeaders listos para los charts.
            commit("SET_CHARTS_DATA", normalizeSalesCharts(response.data, filterPeriod));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit(
                "SET_CHARTS_ERROR",
                extractErrorMessage(error, "Error al cargar gráficos de ventas.")
            );
        } finally {
            commit("SET_CHARTS_LOADING", false);
        }
    },

    // Comparación año-contra-año (lazy: el view la dispara al abrir el panel y
    // en cada cambio de mes/subcategoría/años mientras está abierto). Deriva
    // group_by/item del sub-filtro activo: categoría → filtra a la subcategoría
    // elegida; cuentas → sin filtro (total). El año en curso lo recorta el
    // backend al mes del header.
    async fetchSalesComparison({ state, commit, rootState }) {
        commit("SET_COMPARISON_LOADING", true);
        commit("SET_COMPARISON_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const endYear = moment(endMonth).year();

            // Default a [añoActual, añoActual-1] la primera vez que se abre.
            let years = state.comparisonYears;
            if (!years || years.length === 0) {
                years = [endYear, endYear - 1];
                commit("SET_COMPARISON_YEARS", years);
            }

            // Sólo el modo categoría filtra; cuentas/clientes van sin filtro.
            const sub = state.activeSubFilter;
            const isCategory = sub !== "cuentas" && sub !== "clientes";
            const groupBy = isCategory ? sub : null;
            const item = isCategory ? state.comparisonItem : null;

            const endDate = moment(endMonth).endOf("month").format("YYYY-MM-DD");
            const response = await getSalesComparison(endDate, years, groupBy, item);
            const {
                months,
                series,
                available_years: availableYears,
            } = response.data;

            commit("SET_COMPARISON_DATA", Object.freeze({
                months: Object.freeze(months || []),
                series: Object.freeze((series || []).map(Object.freeze)),
                availableYears: Object.freeze(availableYears || []),
            }));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit(
                "SET_COMPARISON_ERROR",
                extractErrorMessage(error, "Error al cargar la comparación.")
            );
        } finally {
            commit("SET_COMPARISON_LOADING", false);
        }
    },
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
